import type { ChainStepRow, RequirementRow } from '@api/replenishment-engine';
import { GenericChartCard } from '@components/ui/Chart';
import { Badge, Button, Group, Stack, Text } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { useMemo, useState, type FC, type ReactElement } from 'react';

import ExplainWindow from '../ExplainWindow';
import { addDaysKey, dayLabel, localDateKey, whenLabel } from './dates';
import { groupDips, tiersLabel, unlockLabel, waitsOnLabel, type Dip } from './dips';
import classes from './engine.module.css';
import { ApproveGate } from './ApproveGate';
import { LAND_LABEL, STATUS_COLOR } from './status';

type Props = {
    /** Every part of every dip of the SKU, as the detail endpoint returns them. */
    rows: RequirementRow[] | null;
    /** OBJECTIVE: the rows are the dips of one objective's window, all shown. ALL (default): the SKU's horizon, later dips behind a toggle. */
    scope?: 'OBJECTIVE' | 'ALL';
    onClose: () => void;
    onApprove: (ids: string[]) => void;
    onSave?: (ids: string[]) => void;
};

type Point = { day: string; label: string; stock: number; demand: number; inbound: number; planned: number };

const n = (v: number, maxDecimals = 0): string => formatCount(v, { maxDecimals });

const STEP_VERB: Record<string, string> = { MATERIAL: 'Order', TRANSFER: 'Move', WORK_ORDER: 'Start', FG_PO: 'Order', SFG_ON_HAND: 'On hand' };

const Step: FC<{ c: ChainStepRow; index: number; today: string }> = ({ c, index, today }) => (
    <div className={classes.step}>
        <span className={classes.stepIndex}>{index + 1}</span>
        <div className={classes.lines}>
            <div className={classes.line}>
                {STEP_VERB[c.step]} {c.itemName ?? c.itemId} · {n(c.qty, c.unit ? 1 : 0)}
                {c.unit ? ` ${c.unit}` : ''}
            </div>
            <div className={classes.sub}>
                {c.step === 'MATERIAL' ? `need ${n(c.qty, 1)} · on floor ${n(c.available, 1)} · by transfer ${n(c.movable, 1)} · ${c.instrumentId ? `by order ${n(c.short, 1)}` : `short ${n(c.short, 1)}`}` : ''}
                {c.vendorName ? ` · ${c.vendorName}` : ''}
                {c.fromSiteId && c.toSiteId ? ` · ${c.fromSiteId} → ${c.toSiteId}` : ''}
                {c.leadDays != null ? ` · ${c.leadDays}d lead` : ''}
                {c.note ? ` · ${c.note}` : ''}
            </div>
        </div>
        <div className={classes.lines}>
            <div className={classes.line} data-late={c.placeBy && c.placeBy <= today && c.status === 'PROPOSED' ? 'true' : undefined}>
                {c.placeBy ? whenLabel(c.placeBy, today) : ''}
                {c.lands ? ` → lands ${dayLabel(c.lands)}` : ''}
            </div>
            {c.status && (
                <Badge size="xs" variant="light" color={STATUS_COLOR[c.status]} tt="none">
                    {c.status.toLowerCase()}
                </Badge>
            )}
        </div>
    </div>
);

/** One part of a dip's run (one, unless a buy is split across vendors): what it makes of what the curve asked for at its landing, its dates, what unlocked it, and its chain. */
const Part: FC<{ p: RequirementRow; today: string }> = ({ p, today }) => {
    const waits = waitsOnLabel(p.gatingMaterialId, p.chain);
    const land = LAND_LABEL[p.landStatus];
    const own = p.instruments.find((i) => i.id === p.instrumentId);
    const pinned = own?.pinned?.qty != null;
    const over = Number(own?.reason.overCapacity ?? 0);
    return (
        <Stack gap={2}>
            <div className={classes.partHead}>
                <span className={classes.partIndex}>
                    {p.parts > 1 ? `Part ${p.part} of ${p.parts}` : 'Run'}
                </span>
                <Text size="sm" fw={600}>
                    making {n(p.qty)} of {p.neededAtLanding != null ? n(p.neededAtLanding) : '—'} needed by {dayLabel(p.landDate)}
                    {pinned ? ' · fixed by hand' : ''}
                    {over > 0 ? ` · ${n(over)} above what the factory had free that day` : ''}
                </Text>
                <Text size="xs" c="dimmed">
                    {p.route === 'BUY' ? 'orders' : 'starts'} {whenLabel(p.startDate, today)} · lands {dayLabel(p.landDate)} · unlocked by {unlockLabel(p.unlockedBy, p.chain)}
                    {land ? ` · ${land.text}` : ''}
                    {p.stockoutDays > 0 ? ` · ${p.stockoutDays} days at zero before it lands` : ''}
                    {waits ? ` · short of ${waits}` : ''}
                </Text>
                {tiersLabel(p.tiers) && (
                    <Text size="xs" c="dimmed">
                        serves {tiersLabel(p.tiers)}
                    </Text>
                )}
            </div>
            {p.chain.map((c, i) => (
                <Step key={i} c={c} index={i} today={today} />
            ))}
        </Stack>
    );
};

const dipTitle = (d: Dip): string => (d.seq === 0 ? (d.head.landStatus === 'NO_DIP' ? 'Nothing to do' : 'Already covered') : d.seq === 1 ? 'Next replenishment' : `Later replenishment ${d.seq - 1}`);

const landsText = (d: Dip): string => (d.parts.length > 1 && d.lastLand && d.lastLand !== d.firstLand ? `${dayLabel(d.firstLand)} → ${dayLabel(d.lastLand)}` : dayLabel(d.firstLand));

/** The SKU's story: the curve, every dip the run found, and the parts and chain behind each. */
const SkuWindow: FC<Props> = ({ rows, scope = 'ALL', onClose, onApprove, onSave }) => {
    const today = localDateKey();
    const [showLater, setShowLater] = useState(false);
    const dips = useMemo(() => groupDips(rows ?? []), [rows]);
    const first = dips[0]?.head ?? null;
    // The curve rides on part 0 or 1 of the first dip; later parts carry none.
    const series = useMemo(() => (rows ?? []).find((r) => r.series)?.series ?? null, [rows]);
    const points = useMemo<Point[]>(() => {
        if (!series) return [];
        const count = Math.min(series.demand.length, 120);
        const out: Point[] = [];
        for (let i = 0; i < count; i += 1) {
            const d = addDaysKey(series.from, i);
            out.push({ day: d, label: dayLabel(d), stock: Math.max(0, Math.round(series.stock[i] ?? 0)), demand: Math.round(series.demand[i] ?? 0), inbound: Math.round(series.inbound[i] ?? 0), planned: Math.round(series.planned[i] ?? 0) });
        }
        return out;
    }, [series]);
    if (!first || !rows) return null;
    const open = [...new Set(dips.flatMap((d) => d.instruments.filter((i) => i.status === 'PROPOSED').map((i) => i.id)))];
    const later = scope === 'OBJECTIVE' ? 0 : dips.filter((d) => d.seq > 1).length;

    const banner = (
        <div className={classes.banner}>
            <Group gap="lg" wrap="wrap">
                <Text size="sm">
                    On hand <b>{n(first.onHand)}</b>
                    {first.coverDays != null ? ` · ${n(first.coverDays)}d cover` : ''} · DRR <b>{n(first.drr, 1)}</b>/day
                </Text>
                <Text size="sm">
                    Level <b>{first.levelDays}d</b> · refill to <b>{first.refillDays}d</b> · lead <b>{first.longestLeadDays}d</b>
                </Text>
                <Text size="sm">
                    Dips <b>{whenLabel(first.dipDate, today)}</b> · zero <b>{dayLabel(first.zeroDate)}</b> · lands <b>{landsText(dips[0])}</b>
                    {first.stockoutDays > 0 ? ` · ${first.stockoutDays} days at zero` : ''}
                </Text>
            </Group>
        </div>
    );

    const dipBlock = (d: Dip): ReactElement => {
        const h = d.head;
        return (
            <Stack key={d.key} gap={4}>
                <Group gap="xs">
                    <Text fw={700} size="sm">
                        {dipTitle(d)}
                    </Text>
                    <Badge size="xs" variant="light" tt="none">
                        {h.landStatus.replaceAll('_', ' ').toLowerCase()}
                    </Badge>
                    <Text size="xs" c="dimmed">
                        dips {dayLabel(h.dipDate)} · lands {landsText(d)} · {n(h.requiredQty)} units
                        {d.parts.length > 1 ? ` in ${d.parts.length} parts` : ''}
                    </Text>
                </Group>
                {d.parts.length === 0 && (
                    <Text size="sm" c="dimmed">
                        {h.landStatus === 'COVERED' ? `Handled: a landing already on the book (approved or in transit) answers this dip on ${dayLabel(h.landDate)}. Nothing new to order.${h.stockoutDays > 0 ? ` ${h.stockoutDays} days of stockout until then.` : ''}` : h.landStatus === 'UNSOURCEABLE' ? 'No factory, vendor or SFG route is mapped for this SKU.' : h.landStatus === 'NO_DIP' ? (h.dipDate ? `Nothing to plan: the first dip is ${dayLabel(h.dipDate)}, past the furthest window an objective asks about. ${n(h.onHand)} on hand at ${n(h.drr, 1)} a day${h.coverDays != null ? `, ${n(h.coverDays)} days of cover` : ''}.` : `Nothing to plan: the SKU never dips below its ${h.levelDays}-day level inside the horizon. ${n(h.onHand)} on hand at ${n(h.drr, 1)} a day${h.coverDays != null ? `, ${n(h.coverDays)} days of cover` : ''}.`) : 'Nothing to do.'}
                    </Text>
                )}
                {d.parts.map((p) => (
                    <Part key={p.id} p={p} today={today} />
                ))}
            </Stack>
        );
    };

    return (
        <ExplainWindow
            opened
            wide
            onClose={onClose}
            title={first.sku}
            subtitle={first.productName}
            banner={banner}
            footer={
                open.length > 0 ? (
                    <Group justify="flex-end" p="sm">
                        {onSave && (
                            <Button size="xs" variant="subtle" color="grape" title="Keep the chain's numbers as they stand, without creating any document" onClick={() => onSave(open)}>
                                Save
                            </Button>
                        )}
                        <ApproveGate>
                            <Button size="xs" onClick={() => onApprove(open)}>
                                Approve the whole chain ({open.length})
                            </Button>
                        </ApproveGate>
                    </Group>
                ) : undefined
            }
        >
            <Stack gap="md" p="md">
                {points.length > 0 && (
                    <GenericChartCard<Point>
                        kind="line"
                        title="Next 120 days"
                        subtitle="Projected stock with real and approved inbound; planned landings are what this run proposes"
                        data={points}
                        dimensionKey="label"
                        dimensionType="category"
                        series={[
                            { id: 'stock', key: 'stock', label: 'Stock' },
                            { id: 'demand', key: 'demand', label: 'Demand / day', variant: 'dashed' },
                            { id: 'planned', key: 'planned', label: 'Planned landing', renderAs: 'bar' },
                            { id: 'inbound', key: 'inbound', label: 'Inbound', renderAs: 'bar' },
                        ]}
                        height={220}
                    />
                )}
                {dips.filter((d) => scope === 'OBJECTIVE' || d.seq <= 1 || showLater).map(dipBlock)}
                {later > 0 && (
                    <Button size="compact-xs" variant="subtle" onClick={() => setShowLater((v) => !v)}>
                        {showLater ? 'Hide later replenishments' : `${later} later replenishment${later === 1 ? '' : 's'} on the horizon, each sized after the previous one lands · show`}
                    </Button>
                )}
            </Stack>
        </ExplainWindow>
    );
};

export default SkuWindow;
